import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Code, CheckSquare, ShieldCheck, Palette, CloudArrowUp, FileText, Headphones, ChartBar, Sparkle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, CTABanner, CapabilityWheelSVG } from '../components/SiteBlocks';
import { AI_AGENTS } from '../data/content';

const ICONS = { Code, CheckSquare, ShieldCheck, Palette, CloudArrowUp, FileText, Headphones, ChartBar };

export default function AIAgentsPage() {
  const { agentId } = useParams();

  if (agentId) {
    const agent = AI_AGENTS.find(a => a.id === agentId);
    if (!agent) {
      return (
        <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
          <SEO path={`/ai-agents/${agentId}`} title="Agent not found" />
          <Navbar />
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-heading text-4xl font-bold mb-4">Agent not found</h1>
            <Link to="/ai-agents"><Button style={{ background: 'var(--primary-accent)', color: '#fff' }}>Browse all agents →</Button></Link>
          </div>
          <Footer />
        </div>
      );
    }
    return <AgentDetailPage agent={agent} />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path="/ai-agents" title="Our AI Agents — 8 Specialists Per Engagement" description="Meet our 8 specialist AI agents — Dev, Test, Review, UI, DevOps, Docs, Support, Data. Every XceedAgents engineer is augmented by these agents." />
      <Navbar />

      <SectionHero
        eyebrow="AI Agents"
        title="8 specialist AI agents. Every engagement."
        subtitle="Every senior engineer at XceedAgents is augmented by 8 specialist AI agents: Dev, Test, Review, UI, DevOps, Docs, Support, Data. They handle the boilerplate so humans can focus on architecture and intent. The result: 60% faster delivery, fewer bugs, lower cost."
      >
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="agents-cta">Start a Project <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AI_AGENTS.map(a => {
              const Icon = ICONS[a.icon] || Sparkle;
              return (
                <Link key={a.id} to={`/ai-agents/${a.id}`} className="block p-6 rounded-2xl border bg-white hover-lift transition-all" style={{ borderColor: 'var(--border)' }} data-testid={`agent-${a.id}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: 'var(--tag-bg)' }}>
                    <Icon size={20} weight="duotone" style={{ color: a.color }} />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{a.name}</h3>
                  <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: a.color }}>{a.role}</div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{a.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capability wheel */}
      <section className="py-20 lg:py-24 dark-section" style={{ background: 'var(--dark-section-bg)' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="The Hybrid Model" title="One human. Eight agents. Limitless output." dark />
            <p className="mt-6 text-lg leading-relaxed" style={{ color: 'var(--dark-section-muted)' }}>
              Each XceedAgents engineer is the orchestrator of 8 specialist AI agents. Code, tests, reviews, design polish, deployment, documentation, customer support, and analytics — all running in parallel, all reviewed by the human engineer before merge. That's how we deliver 60% faster without sacrificing quality.
            </p>
          </div>
          <CapabilityWheelSVG items={AI_AGENTS.map(a => a.role)} />
        </div>
      </section>

      <CTABanner title="Put our AI agents on your project" ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}

function AgentDetailPage({ agent }) {
  const Icon = ICONS[agent.icon] || Sparkle;
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path={`/ai-agents/${agent.id}`} title={`${agent.name} — ${agent.role}`} description={agent.description || agent.summary} />
      <Navbar />

      <SectionHero eyebrow={agent.role} title={agent.name} subtitle={agent.summary} testId={`agent-hero-${agent.id}`}>
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }}>Start a Project <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <section className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading eyebrow="What it does" title={`How ${agent.name} works`} />
          <p className="mt-6 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>{agent.description}</p>
        </div>
      </section>

      <section className="py-16" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <SectionHeading eyebrow="Capabilities" title="What it can do today" />
            <ul className="mt-6 space-y-3">
              {agent.capabilities.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-base" style={{ color: 'var(--text-primary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full" style={{ background: agent.color }} />
                  {c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="Integrations" title="Plays well with" />
            <div className="mt-6 flex flex-wrap gap-2">
              {agent.integrations.map((i, idx) => (
                <span key={idx} className="px-3 py-1.5 text-sm rounded-md font-medium" style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}>{i}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Other agents" title="Meet the rest of the team" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AI_AGENTS.filter(a => a.id !== agent.id).map(a => {
              const I = ICONS[a.icon] || Sparkle;
              return (
                <Link key={a.id} to={`/ai-agents/${a.id}`} className="block p-5 rounded-xl border bg-white hover-lift" style={{ borderColor: 'var(--border)' }}>
                  <I size={20} weight="duotone" style={{ color: a.color }} className="mb-3" />
                  <h4 className="font-heading font-bold text-base" style={{ color: 'var(--text-primary)' }}>{a.name}</h4>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.role}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner title={`Put ${agent.name} on your project`} ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}
